'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { Project } from '@/features/projects/types';
import { useAuth } from '@/contexts/AuthContext';
import { projectService } from '@/services/projects';

interface WorkflowStage {
  id: string;
  label: string;
  path: string;
  icon: string;
  completed: boolean;
}

interface NavigationState {
  currentProject: Project | null;
  projects: Project[];
  projectsLoading: boolean;
  projectSelectionReady: boolean;
  workflowStages: WorkflowStage[];
  activeStage: string;
  hasUnsavedChanges: boolean;
  isProjectSelectorOpen: boolean;
  isMobileMenuOpen: boolean;
}

interface NavigationContextType extends NavigationState {
  setCurrentProject: (project: Project | null) => void;
  refreshProjects: () => Promise<void>;
  setActiveStage: (stageId: string) => void;
  updateStageCompletion: (stageId: string, completed: boolean) => void;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  toggleProjectSelector: () => void;
  toggleMobileMenu: () => void;
  closeProjectSelector: () => void;
  closeMobileMenu: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

const defaultWorkflowStages: WorkflowStage[] = [
  {
    id: 'build',
    label: 'Build',
    path: '/ai-wizard',
    icon: 'PencilSquareIcon',
    completed: false,
  },
  {
    id: 'preview',
    label: 'Preview',
    path: '/preview',
    icon: 'EyeIcon',
    completed: false,
  },
  {
    id: 'export',
    label: 'Export',
    path: '/deploy',
    icon: 'ArrowDownTrayIcon',
    completed: false,
  },
];

export function NavigationProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { user } = useAuth();
  const [projectParam, setProjectParam] = useState<string | null>(null);

  const [state, setState] = useState<NavigationState>({
    currentProject: null,
    projects: [],
    projectsLoading: false,
    projectSelectionReady: false,
    workflowStages: defaultWorkflowStages,
    activeStage: pathname === '/overview' || pathname === '/dashboard' ? 'dashboard' : 'build',
    hasUnsavedChanges: false,
    isProjectSelectorOpen: false,
    isMobileMenuOpen: false,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const readProjectParam = () => {
      setProjectParam(new URLSearchParams(window.location.search).get('project'));
    };

    readProjectParam();
    window.addEventListener('popstate', readProjectParam);
    return () => window.removeEventListener('popstate', readProjectParam);
  }, [pathname]);

  // Load projects when user is available
  const loadProjects = useCallback(async () => {
    if (!user) return;
    setState(prev => ({ ...prev, projectsLoading: true, projectSelectionReady: false }));
    try {
      const data = await projectService.getUserProjects(user.id);
      setState(prev => ({ ...prev, projects: data || [], projectsLoading: false }));
    } catch {
      setState(prev => ({ ...prev, projects: [], projectsLoading: false }));
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadProjects();
    } else {
      setState(prev => ({ ...prev, projects: [], currentProject: null, projectsLoading: false, projectSelectionReady: true }));
    }
  }, [user, loadProjects]);

  // Restore selected project from the URL first, then localStorage.
  useEffect(() => {
    if (!user || state.projectsLoading) return;

    if (state.projects.length === 0) {
      setState(prev => ({ ...prev, currentProject: null, projectSelectionReady: true }));
      return;
    }

    if (projectParam) {
      const projectFromUrl = state.projects.find(p => p.id === projectParam);
      if (projectFromUrl && state.currentProject?.id !== projectFromUrl.id) {
        setState(prev => ({ ...prev, currentProject: projectFromUrl, projectSelectionReady: true }));
      } else if (!projectFromUrl && state.currentProject) {
        setState(prev => ({ ...prev, currentProject: null, projectSelectionReady: true }));
      } else {
        setState(prev => ({ ...prev, projectSelectionReady: true }));
      }
      return;
    }

    if (state.currentProject && state.projects.some(p => p.id === state.currentProject?.id)) {
      setState(prev => ({ ...prev, projectSelectionReady: true }));
      return;
    }

    const storedId = typeof window !== 'undefined'
      ? localStorage.getItem('waitlistly:selected-project-id')
      : null;
    if (storedId) {
      const found = state.projects.find(p => p.id === storedId);
      if (found) {
        setState(prev => ({ ...prev, currentProject: found, projectSelectionReady: true }));
        return;
      }
      localStorage.removeItem('waitlistly:selected-project-id');
    }

    setState(prev => ({ ...prev, currentProject: null, projectSelectionReady: true }));
  }, [user, projectParam, state.projectsLoading, state.projects, state.currentProject]);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  const setCurrentProject = useCallback((project: Project | null) => {
    if (typeof window !== 'undefined') {
      if (project) {
        localStorage.setItem('waitlistly:selected-project-id', project.id);
      } else {
        localStorage.removeItem('waitlistly:selected-project-id');
      }
    }
    setState(prev => ({ ...prev, currentProject: project, projectSelectionReady: true }));
  }, []);

  const setActiveStage = useCallback((stageId: string) => {
    setState(prev => ({ ...prev, activeStage: stageId }));
  }, []);

  const updateStageCompletion = useCallback((stageId: string, completed: boolean) => {
    setState(prev => ({
      ...prev,
      workflowStages: prev.workflowStages.map(stage =>
        stage.id === stageId ? { ...stage, completed } : stage
      ),
    }));
  }, []);

  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setState(prev => ({ ...prev, hasUnsavedChanges: hasChanges }));
  }, []);

  const toggleProjectSelector = useCallback(() => {
    setState(prev => ({
      ...prev,
      isProjectSelectorOpen: !prev.isProjectSelectorOpen,
      isMobileMenuOpen: false,
    }));
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setState(prev => ({
      ...prev,
      isMobileMenuOpen: !prev.isMobileMenuOpen,
      isProjectSelectorOpen: false,
    }));
  }, []);

  const closeProjectSelector = useCallback(() => {
    setState(prev => ({ ...prev, isProjectSelectorOpen: false }));
  }, []);

  const closeMobileMenu = useCallback(() => {
    setState(prev => ({ ...prev, isMobileMenuOpen: false }));
  }, []);

  const value: NavigationContextType = {
    ...state,
    setCurrentProject,
    refreshProjects,
    setActiveStage,
    updateStageCompletion,
    setHasUnsavedChanges,
    toggleProjectSelector,
    toggleMobileMenu,
    closeProjectSelector,
    closeMobileMenu,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
